import { useEffect, useRef, useState, type ElementType, type FC } from "react";
import { ChevronLeft, PenLine, Plus, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CampaignChannel, CampaignData, LocationState } from "../../EngagementInterfaces";
import {
  AI_EMAIL_EDITOR_ROUTE,
  TEMPLATE_EMAIL_EDITOR_ROUTE,
} from "../CampaignSetup/CampaignSetupConstants";
import "../Styles/CreateCampaignMenu.scss";

const CREATE_CAMPAIGN_ROUTE = "/account/engagement/create-campaign";
const MENU_KEY = "campaignDashboard.createMenu";

type IconComponent = ElementType;

const NEW_EMAIL_CAMPAIGN: CampaignData = {
  channel: "email",
  campaignName: "",
  sendTo: [],
  excludeList: [],
  companyNameEngagements: "",
};

const CHANNELS: { channel: CampaignChannel }[] = [{ channel: "email" }, { channel: "whatsapp" }];

const CREATE_MODES: { useAi: boolean; Icon: IconComponent; labelKey: string; descKey: string }[] = [
  { useAi: true, Icon: Sparkles, labelKey: "withAi", descKey: "withAiDesc" },
  { useAi: false, Icon: PenLine, labelKey: "withoutAi", descKey: "withoutAiDesc" },
];

const CreateCampaignMenu: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState<CampaignChannel | null>(null);

  const closeMenu = () => {
    setIsOpen(false);
    setActiveChannel(null);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const startCampaign = (state: Pick<LocationState, "channel" | "use_ai">) => {
    closeMenu();
    if (state.channel === "email") {
      const route = state.use_ai ? AI_EMAIL_EDITOR_ROUTE : TEMPLATE_EMAIL_EDITOR_ROUTE;
      navigate(route, { state: { campaignData: NEW_EMAIL_CAMPAIGN } });
      return;
    }
    navigate(CREATE_CAMPAIGN_ROUTE, { state });
  };

  return (
    <div className="campaign-dashboard__create-menu create-campaign-menu" ref={containerRef}>
      <button
        type="button"
        className="button button--primary campaign-dashboard__create-btn"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? closeMenu() : setIsOpen(true))}
      >
        <Plus size={16} aria-hidden />
        {t("campaignDashboard.createNewCampaign")}
      </button>

      {isOpen ? (
        <div className="create-campaign-menu__panel" role="menu">
          <div className="create-campaign-menu__items">
          {CHANNELS.map(({ channel }) => {
            const isActive = activeChannel === channel;
            return (
              <button
                key={channel}
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={isActive}
                className={`create-campaign-menu__item ${
                  isActive ? "create-campaign-menu__item--active" : ""
                }`}
                onClick={() => setActiveChannel(isActive ? null : channel)}
                onMouseEnter={() => setActiveChannel(channel)}
                onFocus={() => setActiveChannel(channel)}
              >
                <ChevronLeft size={14} className="create-campaign-menu__chevron" aria-hidden />
                <span className="create-campaign-menu__text">
                  <span className="create-campaign-menu__title">
                    {t(`${MENU_KEY}.${channel}.title`)}
                  </span>
                  <span className="create-campaign-menu__subtitle">
                    {t(`${MENU_KEY}.${channel}.subtitle`)}
                  </span>
                </span>
              </button>
            );
          })}
          </div>

          {activeChannel ? (
            <div className="create-campaign-menu__submenu" role="menu">
              <p className="create-campaign-menu__submenu-heading">
                {t(`${MENU_KEY}.${activeChannel}.prompt`)}
              </p>
              {CREATE_MODES.map(({ useAi, Icon, labelKey, descKey }) => (
                <button
                  key={labelKey}
                  type="button"
                  role="menuitem"
                  className="create-campaign-menu__option"
                  onClick={() => startCampaign({ channel: activeChannel, use_ai: useAi })}
                >
                  <span className="create-campaign-menu__icon" aria-hidden>
                    <Icon size={16} />
                  </span>
                  <span className="create-campaign-menu__text">
                    <span className="create-campaign-menu__title">
                      {t(`${MENU_KEY}.${labelKey}`)}
                      {useAi ? (
                        <span className="create-campaign-menu__badge">
                          {t(`${MENU_KEY}.recommended`)}
                        </span>
                      ) : null}
                    </span>
                    <span className="create-campaign-menu__subtitle">
                      {t(`${MENU_KEY}.${activeChannel}.${descKey}`)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CreateCampaignMenu;
